/**
 * O resumo do prontuário do paciente e a marca de acesso (#811, ADR-0022).
 *
 * O prontuário fica no sistema da clínica; aqui só o resumo de cada
 * atendimento. Só nas organizações com a funcionalidade "clinica" ligada, e
 * só para quem tem a marca "acesso ao prontuário".
 */

import { z } from 'zod';

export const AtendimentoDoProntuarioSchema = z.object({
  id: z.string().uuid(),
  /** O sistema de onde o atendimento veio (`sisac`). */
  origem: z.string(),
  /** O registro no sistema de origem, onde o prontuário completo está. */
  codigo: z.string(),
  /**
   * Quando. O que vem do SISAC ao vivo (#1144) é a hora da clínica, sem fuso,
   * como no resto do paciente: a tela mostra como veio.
   */
  realizado_em: z.string().datetime({ offset: true, local: true }),
  /** Como a origem chama: "Consulta", "Retorno", "Procedimento"… */
  tipo: z.string(),
  /** Quem atendeu, ou `null` se a origem não disse. */
  profissional: z.string().nullable(),
});
export type AtendimentoDoProntuario = z.infer<typeof AtendimentoDoProntuarioSchema>;

export const ProntuarioDoPacienteSchema = z.object({
  /**
   * Do mais recente ao mais antigo: os agendamentos e as consultas, e os exames
   * feitos, lidos agora do SISAC pelo paciente vinculado (#1144).
   */
  itens: z.array(AtendimentoDoProntuarioSchema),
  /**
   * O que a tela precisa dizer quando a lista vem curta:
   * - `ok`: o SISAC respondeu;
   * - `sem_vinculo`: o cliente não está vinculado a um paciente do SISAC (a
   *   recepção vincula no atendimento, em "Paciente no SISAC");
   * - `sem_partes`: a agenda da clínica não tem as partes do histórico ligadas;
   * - `fora`: o SISAC não respondeu;
   * - `parcial`: uma das leituras falhou, e veio só a outra.
   */
  sisac: z.enum(['ok', 'sem_vinculo', 'sem_partes', 'fora', 'parcial']),
});
export type ProntuarioDoPaciente = z.infer<typeof ProntuarioDoPacienteSchema>;

export const AcessoAoProntuarioSchema = z.object({ ligado: z.boolean() }).strict();
export type AcessoAoProntuario = z.infer<typeof AcessoAoProntuarioSchema>;
